import { lazy } from "react";
import { Navigate, Route } from "react-router";
import { App } from "./app";
import { ApplicationLayout } from "./modules/layout/components/application-layout";
import { GenerateLayout } from "./modules/layout/components/generate-layout";
import { ContentLibraryLayout } from "./modules/layout/components/content-library-layout";
import { CeAdminLayout, Layout } from "./modules/layout/components/layout";
import AdditionalServices from "./pages/ce-admin/additional-services";
import CeAdminDesignHuddleBrandPage from "./pages/ce-admin/ce-admin-design-huddle-brand-page";
import CeAdminPage from "./pages/ce-admin/ce-admin-page";
import CreateAccount from "./pages/ce-admin/create-account-page";
import ManageDiscoverPage from "./pages/ce-admin/manage-discover-page";
import EmailAddressConfigurationPage from "./pages/channels/email/email-address-configuration-page";
import EmailDomainConfigurationPage from "./pages/channels/email/email-domain-configuration-page";
import SlackPage from "./pages/channels/slack-page";
import AssetPage from "./pages/content-library/asset-page";
import ContentLibraryPage from "./pages/content-library/content-library-page";
import CreateContentEditPage from "./pages/create-content/create-content-edit-page";
import CreateContentPage from "./pages/create-content/create-content-page";
import CreateContentPreviewPage from "./pages/create-content/create-content-preview-page";
import DiscoverPage from "./pages/discover/discover-page";
import DiscoverProgramPage from "./pages/discover/discover-program-page";
import DiscoverSearchPage from "./pages/discover/discover-search-page";
import DiscoverTemplatePage from "./pages/discover/discover-template-preview-page";
import EventsPage from "./pages/events/events-page";
import FollowUpPage from "./pages/follow-up/follow-up-page";
import LogoutPage from "./pages/logout/logout-page";
import EmployeeFieldsPage from "./pages/manage/employee-fields-page";
import ManageChannelsPage from "./pages/manage/manage-channels-page";
import ManageEmployeesPage from "./pages/manage/manage-employees-page";
import ManageEventsAndTagsPage from "./pages/manage/manage-events-and-tags-page";
import ManageProgramsPage from "./pages/manage/manage-programs-page";
import ManageSegmentsPage from "./pages/manage/manage-segments-page";
import CopyMomentPage from "./pages/moments/copy/copy-moment-page";
import GenerateCommsPage from "./pages/moments/generate/generate-comms-page";
import SentMomentsPage from "./pages/moments/history/sent-moments-page";
import NewMomentPage from "./pages/moments/new-moment-page";
import NotFound from "./pages/not-found";
import OutboxRecipientPage from "./pages/outbox-recipient-page";
import RecipientActivityPage from "./pages/people/recipient-activity-page";
import TabbedProgramsPage from "./pages/tabbed-programs-page";
import TabbedProgramPage from "./pages/tabbed-programs-page/tabbed-program-page";
import TimelinePage from "./pages/timeline/timeline-page";
import { ProtectedRoute } from "./protected-route";

const MomentFormPage = lazy(() => import("./pages/moments/moment-form-page"));
const Calendar4Page = lazy(() => import("./pages/calendar/calendar4-page"));
const PersonPage = lazy(() => import("./pages/people/person-page"));
const AnalyticsPage = lazy(() => import("./pages/analytics/analytics-page"));
const BrandKitPage = lazy(() => import("./pages/brand-kit/brand-kit-page"));
const ListenApp = lazy(() => import("listen/App"));

export const Routes = (
  <>
    <Route path="/" element={<App />}>
      <Route element={<GenerateLayout />}>
        <Route path="/generate-comms" element={<GenerateCommsPage />} />
      </Route>

      <Route element={<ProtectedRoute Component={Layout} themeOverride="light" />}>
        <Route path="/listen/*" element={<ListenApp />} />
      </Route>

      <Route element={<ProtectedRoute Component={Layout} />}>
        <Route index element={<CreateContentPage />} />
        <Route path="/moments/:id" element={<MomentFormPage />} />
        <Route path="/moments/:id/*" element={<MomentFormPage />} />
        <Route path="/moments/:id/newsletter" element={<MomentFormPage isNewsletter />} />
        <Route path="/moments/:id/newsletter/*" element={<MomentFormPage isNewsletter />} />
        <Route path="/moments/dashboard" element={<Navigate replace to="/" />} />
        <Route path="/tabbed-programs" element={<TabbedProgramsPage />} />
        <Route path="/tabbed-programs/:id" element={<TabbedProgramPage />} />
        <Route path="/moments" element={<NewMomentPage />} />
        <Route path="/moments/newsletter" element={<NewMomentPage newsletter />} />
        <Route path="/moments/copy/:id" element={<CopyMomentPage />} />
        <Route path="/create-content" element={<CreateContentPage />} />
        <Route path="/create-moment" element={<Navigate replace to="/create-content" />} />
        <Route path="/create-content/preview" element={<CreateContentPreviewPage />} />
        <Route path="/timeline/:id" element={<TimelinePage />} />
        <Route path="/calendars/*" element={<Calendar4Page />} />

        <Route path="/moments/history" element={<SentMomentsPage />} />

        {/* Superadmin */}
        <Route
          path="/analytics/*"
          element={<ProtectedRoute type="super_admin" Component={AnalyticsPage} />}
        />

        <Route
          path="/recipient-activity"
          element={<ProtectedRoute type="super_admin" Component={RecipientActivityPage} />}
        />
        <Route
          path="/recipient-activity/:person_id/*"
          element={<ProtectedRoute type="super_admin" Component={PersonPage} />}
        />
        <Route
          path="/channels/email/custom-address"
          element={<ProtectedRoute type="super_admin" Component={EmailAddressConfigurationPage} />}
        />
        <Route
          path="/channels/email/custom-domain"
          element={<ProtectedRoute type="super_admin" Component={EmailDomainConfigurationPage} />}
        />
        <Route
          path="/channels/slack"
          element={<ProtectedRoute type="super_admin" Component={SlackPage} />}
        />

        <Route
          path="/manage/employees/*"
          element={<ProtectedRoute type="super_admin" Component={ManageEmployeesPage} />}
        />
        <Route
          path="/manage/channels/*"
          element={<ProtectedRoute type="super_admin" Component={ManageChannelsPage} />}
        />
        <Route
          path="/manage/employee_data/*"
          element={<ProtectedRoute type="super_admin" Component={ManageEventsAndTagsPage} />}
        />
        <Route
          path="/events"
          element={<ProtectedRoute type="super_admin" Component={EventsPage} />}
        />
        <Route
          path="/manage/segments/*"
          element={<ProtectedRoute type="super_admin" Component={ManageSegmentsPage} />}
        />
        <Route
          path="/manage/fields"
          element={<ProtectedRoute type="super_admin" Component={EmployeeFieldsPage} />}
        />
        <Route
          path="/manage/programs"
          element={<ProtectedRoute type="super_admin" Component={ManageProgramsPage} />}
        />
        <Route
          path="/manage/brand/*"
          element={<ProtectedRoute type="super_admin" Component={BrandKitPage} />}
        />
        <Route
          path="/outbox"
          element={<ProtectedRoute type="super_admin" Component={OutboxRecipientPage} />}
        />

        <Route path="/discover/:program_slug/:template_slug" element={<DiscoverTemplatePage />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route element={<ProtectedRoute Component={ApplicationLayout} layout="content" />}>
        <Route path="/follow-up/:id" element={<FollowUpPage />} />
        <Route
          path="/follow-up/moment/:moment_id"
          element={<FollowUpPage config="from_moment" />}
        />
        <Route
          path="/follow-up/moment/:moment_id/survey/:survey_id"
          element={<FollowUpPage config="from_survey" />}
        />
        <Route path="/create-content/edit" element={<CreateContentEditPage />} />
        <Route path="/content-library/:id" element={<AssetPage />} />
      </Route>

      <Route element={<ProtectedRoute Component={ContentLibraryLayout} />}>
        <Route path="/content-library" element={<ContentLibraryPage />} />
      </Route>

      <Route element={<ProtectedRoute Component={Layout} themeOverride="dark" />}>
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/discover/search" element={<DiscoverSearchPage />} />
        <Route path="/discover/:program_slug" element={<DiscoverProgramPage />} />
      </Route>

      <Route element={<ProtectedRoute type="ce_admin" Component={CeAdminLayout} />}>
        <Route path="/ce_admin" element={<CeAdminPage />} />
        <Route path="/ce_admin/create_account" element={<CreateAccount />} />
        <Route path="/ce_admin/manage_discover" element={<ManageDiscoverPage />} />
        <Route
          path="/ce_admin/design_huddle_custom_brands"
          element={<CeAdminDesignHuddleBrandPage />}
        />
        <Route path="/ce_admin/additional_services" element={<AdditionalServices />} />

        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/logout" element={<LogoutPage />} />
    </Route>
  </>
);
